<template>
  <a-modal
    :visible="visible"
    :title="importTypeName"
    width="640px"
    centered
    :body-style="{ padding: '12px 20px' }"
    :mask-closable="false"
    :keyboard="false"
    @cancel="handleCancel"
  >
    <p v-if="typeId == ImportType.ContactsSequence">
      Carga los contactos que deseas asignar a tus secuencias. Asegúrate de que el ID de la
      secuencia esté asociado a la campaña correspondiente.
    </p>
    <p v-else>
      Selecciona donde quieres importar tus datos masivamente. Visita nuestro
      <InfoDrawer :item-scope="itemScope" text="centro de ayuda" />
      para conocer nuestros consejos para una importación exitosa.
    </p>

    <a-form ref="formRef" :model="formState" :rules="rules" layout="vertical" hide-required-mark>
      <a-row :gutter="24">
        <a-col :span="24" :offset="offset" v-if="typeOperation === TypeBusiness.contactCenter">
          <a-form-item label="Empresa" name="entity">
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.entity"
              placeholder="Seleccione una empresa"
              @change="handleEntityChange"
            >
              <a-select-option v-for="item in entities" :key="item.id">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>

        <a-col :span="24" :offset="offset" v-if="true">
          <a-form-item
            label="País de los números a importar"
            name="country"
            v-if="showCountryPhone"
          >
            <SelectorCountry
              :code="formState.country"
              @selected="formState.country = $event"
              @codePhone="code_phone = $event"
              hasMixOption
            />

            <p class="info-text" v-if="code_phone !== undefined">
              <template v-if="code_phone === 0">
                Recuerda que, la columna <strong>code_country es obligatoria.</strong> Si colocas un
                código que corresponde a varios países, el sistema asignará automáticamente uno por
                defecto.
              </template>

              <template v-else>
                Recuerda que, debes colocar el número <strong>{{ code_phone }}</strong> en la
                columna <strong>”code_country”</strong> de todas las celdas que desees importar.
              </template>
            </p>
          </a-form-item>
        </a-col>

        <a-col
          :span="enableBases && typeOperation === TypeBusiness.contactCenter ? '12' : '24'"
          v-if="showCampaign"
        >
          <a-form-item label="Campaña" name="portfolio">
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.portfolio"
              placeholder="Seleccione una campaña"
              @change="handlePortfolioChange"
            >
              <a-select-option v-for="item in portfolios" :key="item.id">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col
          :span="typeOperation === TypeBusiness.contactCenter ? '12' : '24'"
          v-if="enableBases && showCampaign"
        >
          <a-form-item label="Base" name="base">
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.base"
              placeholder="Seleccione una base"
            >
              <a-select-option v-for="item in bases" :key="item.id">
                {{ item.name }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
      </a-row>

      <a-form-item v-if="hasActionType" label="Acción a realizar" name="action">
        <a-select
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          v-model:value="formState.action"
          placeholder="Seleecionar acción a realizar"
        >
          <a-select-option :value="0">Activar</a-select-option>
          <a-select-option :value="1">Inactivar</a-select-option>
        </a-select>
      </a-form-item>
    </a-form>

    <div class="template-download">
      <div class="description">
        <h3>Formato para importación</h3>
        <p>Se descargará un archivo XLSX para cargar tu información.</p>
      </div>

      <a-button @click="handleDownload"> Descargar archivo </a-button>
    </div>

    <div>
      <span>Carga tu archivo aquí</span>
      <archive-picker :value="file" @update:value="file = $event" :maxMBSize="3" />
    </div>

    <template v-slot:footer>
      <a-button key="back" @click="handleReturnModal"> Volver </a-button>
      <a-button key="submit" type="primary" :loading="loading" :disabled="!file" @click="handleOk">
        Importar
      </a-button>
    </template>
  </a-modal>
</template>

<script>
import { defineComponent, ref, reactive, toRaw, watch, computed } from "vue";
import { useStore } from "vuex";

import { fetchPortfolios, fetchAllBases } from "@/app/entities/services";
import { ImportTypeName } from "@/app/imports/utils/constants";
import { ImportType } from "@/app/imports/utils/enums";
import { TypeBusiness } from "@/app/shared/utils/enums";
import { templates } from "@/app/imports/utils/templates";
import { clientOptions, otherOptions } from "@/app/imports/utils/massiveProcess";

import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import SelectorCountry from "@/app/shared/components/selectors/SelectorCountry.vue";
import items from "@/app/common/components/organisms/TheSidebar/items";
import InfoDrawer from "@/app/common/components/organisms/InfoDrawer.vue";

export default defineComponent({
  components: {
    ArchivePicker,
    SelectorCountry,
    InfoDrawer,
  },
  props: {
    modelValue: {
      required: true,
      type: Boolean,
    },
    importType: {
      required: true,
      type: Number,
    },
    isTypeBusiness: {
      required: true,
      type: Boolean,
    },
  },
  setup() {
    const formRef = ref();
    const formState = reactive({
      entity: undefined,
      portfolio: undefined,
      base: undefined,
      action: undefined,
      country: undefined,
    });
    const store = useStore();
    const user = computed(() => store.getters["getDataUser"]);
    watch(user, (newValue) => {
      if (newValue.customer.type_business == 2) {
        formState.entity = 1;
        formState.portfolio = undefined;
        formState.base = undefined;
      }
    });
    const rules = {
      entity: [{ required: true, message: "Campo requerido" }],
      portfolio: [{ required: true, message: "Campo requerido" }],
      base: [{ required: true, message: "Campo requerido" }],
      action: [{ required: true, message: "Campo requerido" }],
    };

    return {
      formRef,
      formState,
      rules,
      user,
      ImportType,
    };
  },
  data() {
    return {
      loading: false,
      file: null,
      portfolios: [],
      bases: [],
      TypeBusiness,
      code_phone: undefined,
    };
  },
  created() {
    if (this.isTypeBusiness) {
      this.formState.entity = this.entities.find((item) => item.is_owner).id;
      this.fetchPortfolios();
    }
  },
  computed: {
    entities() {
      return this.$store.getters["getEntityList"];
    },
    enableBases() {
      return this.user?.customer.enable_bases;
    },
    typeOperation() {
      return this.user?.customer?.type_business;
    },
    item() {
      const path = this.$route.path;
      return items.find((item) => path.includes(item.route));
    },
    subItem() {
      const path = this.$route.path;
      return this.item?.subitems?.find((subItem) => path.includes(subItem?.route));
    },
    itemScope() {
      return this.subItem?.scope || this.item?.scope;
    },
    showInfo() {
      const availableScopes = ["tab_imports"];

      return availableScopes.includes(this.itemScope);
    },
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.file = null;
        this.$emit("update:modelValue", value);
      },
    },
    typeId() {
      const modal = [...clientOptions, ...otherOptions].find(
        (opt) => opt.title === this.importType
      );

      // return Array.isArray(modal.type) ? modal.type[0] : modal.type;
      return modal.type;
    },
    hasActionType() {
      return Array.isArray(this.typeId);
    },
    selectedType() {
      return this.hasActionType ? this.typeId[this.formState.action ?? 0] : this.typeId;
    },
    importTypeName() {
      if (!this.typeId) return;

      return ImportTypeName[this.selectedType];
    },
    templateDownloadLink() {
      if (!this.typeId) return;

      const templatesList = [...templates.clients, ...templates.others];

      const currentTemplate = templatesList.find((template) =>
        Array.isArray(template.type)
          ? template.type.includes(this.selectedType)
          : template.type === this.selectedType
      );

      return currentTemplate?.url;
    },
    showCountryPhone() {
      const values = [
        ImportType.Phones,
        ImportType.Contacts,      
      ];
      return values.includes(this.selectedType);
    },
    showCampaign() {
      const values = [
        ImportType.AgentsAssignment,
        ImportType.Payments,
        ImportType.ProductsDetail,
        ImportType.ExternalTypifications,
        ImportType.ActivateAccounts,
        ImportType.InactivateAccounts,
        ImportType.ActiveClients,
        ImportType.InactiveClients,
        ImportType.ContactsSequence,
      ];
      return values.includes(this.selectedType);
    },
    showBase() {
      const values = [
        ImportType.Payments,
        ImportType.ProductsDetail,
        ImportType.ExternalTypifications,
        ImportType.ActivateAccounts,
        ImportType.InactivateAccounts,
        ImportType.ActiveClients,
        ImportType.InactiveClients,
      ];
      return values.includes(this.selectedType);
    },
    offset() {
      if (this.showBase && this.showCampaign) return 0;
      else if (!this.showBase && !this.showCampaign) return 8;
      return 4;
    },
  },
  methods: {
    handleDownload() {
      const a = document.createElement("a");
      a.href = this.templateDownloadLink;
      a.download = true;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    handleOk() {
      this.formRef.validateFields().then(() => {
        const { entity, portfolio, base } = toRaw(this.formState);
        const type_import = this.selectedType;
        const file_import = this.file;
        const data = { entity, type_import, file_import };

        if (this.showCampaign) {
          data.portfolio = portfolio;
        }

        if (this.showBase) {
          data.base = base;
        }

        if (this.formState.country) data.country = this.formState.country;

        this.uploadData(data);
      });
    },
    async uploadData(data) {
      try {
        this.loading = true;
        await this.$store.dispatch("importMasiveProcessTemplate", data);
        // this.message
      } catch {
        // this.message
      } finally {
        this.visible = false;
        this.loading = false;
      }
    },
    handleCancel() {
      this.visible = false;
    },
    async fetchPortfolios() {
      const data = await fetchPortfolios("", "", this.formState.entity);
      this.portfolios = data.results;
    },
    async fetchBases() {
      const data = await fetchAllBases(
        "",
        this.formState.entity,
        this.formState.portfolio,
        {},
        "1"
      );
      this.bases = data.results;
      this.formState.base = this.bases[0].id;
    },
    handleEntityChange() {
      // this.formRef.portfolio = undefined;
      // this.formRef.base = undefined;

      if (this.showCampaign) {
        this.fetchPortfolios();
      }
    },
    handlePortfolioChange() {
      this.formRef.base = undefined;
      this.fetchBases();
    },
    handleReturnModal() {
      this.$emit("onModalProcessMasive");
    },
  },
});
</script>

<style lang="sass" scoped>
.picker
  &__wrapper
    max-width: 420px
    margin-top: 20px
    margin-bottom: 20px

.info-text
  margin: 0
  color: $gray-7
  line-height: 22px

:deep(.ant-col-offset-8)
  margin-left: 0

:deep(.ant-col-offset-4)
  margin-left: 0 !important

.template-download
  padding: 10px
  background-color: $gray-2
  border-radius: 8px
  display: flex
  justify-content: space-between
  align-items: center
  margin-bottom: 24px

  .description
    color: $gray-8

    & > *
      margin: 0

    h3
      font-size: 14px
      font-weight: 600
      margin-bottom: 4px

    p
      font-size: 12px
</style>
